import React, { useState } from "react";

import FormSubmit from "ui/FormSubmit";
import Panel from "ui/Panel";
import { useSetFlash } from "providers/WithFlashes";
import { WithCurrentOrganizationQuery } from "providers/WithCurrentOrganization/gql/Query.generated";

import { useUpdateServerGroupMutation } from "./gql/Mutation.update.generated";

type OrganizationType =
  WithCurrentOrganizationQuery["getCurrentUserDetails"]["organizations"][number];

type Props = {
  serverGroup: OrganizationType["serverGroups"][number];
  currentOrganization: OrganizationType;
};

const EditForm = ({ serverGroup }: Props) => {
  const initialName =
    serverGroup.name === serverGroup.clusterId ? "" : serverGroup.name;
  const [name, setName] = useState(initialName);
  const setFlash = useSetFlash();
  const [updateServerGroup, { loading }] = useUpdateServerGroupMutation();

  const disabled = !serverGroup.permittedToEdit;
  const clusterIdLabel = /^\d+$/.test(serverGroup.clusterId)
    ? "System identifier"
    : "Cluster identifier";

  const handleSave = () => {
    updateServerGroup({
      variables: { serverGroupId: serverGroup.id, name: name.trim() },
      onError: (error) => {
        setFlash("error", `Failed to update server group: ${error.message}`);
      },
      onCompleted: (data) => {
        const errors = data.updateServerGroup.errors;
        if (errors && errors.length > 0) {
          setFlash("error", errors.join(", "));
          return;
        }
        setFlash("success", "Server group updated");
      },
    });
  };

  return (
    <Panel title={`Cluster ${serverGroup.name}`}>
      <div className="p-[10px]">
        <div className="form-group">
          <label className="control-label" htmlFor="server_group_name">
            Name
          </label>
          <div className="row">
            <div className="col-sm-5">
              <input
                id="server_group_name"
                className="form-control"
                type="text"
                value={name}
                disabled={disabled}
                placeholder={serverGroup.clusterId}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="col-sm-7">
              <div className="help-block">
                {disabled ? (
                  <>
                    {clusterIdLabel}:{" "}
                    <code className="break-all">{serverGroup.clusterId}</code>
                  </>
                ) : (
                  <>
                    Leave blank to use the {clusterIdLabel}{" "}
                    <code className="break-all">{serverGroup.clusterId}</code>{" "}
                    as the display name.
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <FormSubmit
          title="Save"
          onClick={handleSave}
          disabled={disabled || loading}
        />
      </div>
    </Panel>
  );
};

export default EditForm;
